import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { Fieldset } from '@components/Fields/Fieldset';
import { TelefoneField } from '@components/Fields/TelefoneField';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { TableHeaderCell } from '@components/Table/TableHeaderCell';
import { TableKeyCell } from '@components/Table/TableKeyCell';
import { VerticalGrid } from '@components/VerticalGrid';
import { useData } from '@hooks/useData';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useFormatter from '@hooks/useFormatter';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { IconButton, makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import PhoneIcon from '@material-ui/icons/Phone';
import ILinha from '@model/Linha';
import { IVendedor } from '@model/Vendedor';
import { useLinhaService } from '@services/LinhaService';
import { useVendedorMovimentacaoService } from '@services/VendedorMovimentacaoService';
import { useThemeColor } from '@themes/Colors';
import clsx from 'clsx';
import React from 'react';

export type AtribuirModalProps = {
    modal: IModalWindow
    vendedor: IVendedor
    onAtribuicaoRegistrada: VoidFunction
}

export function AtribuirModal ({ modal, vendedor, onAtribuicaoRegistrada }: AtribuirModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const telefoneFieldRef = React.createRef<HTMLInputElement>();
    const cls = useStyles();
    const themeColor = useThemeColor();
    const formatter = useFormatter();

    const vendedorMovimentacaoService = useVendedorMovimentacaoService();
    const linhaService = useLinhaService();
    
    const data = useData<ILinha>();

    const itensForm = useForm<Partial<ILinha>>({
        numeroSemMascara: useField(),
    });

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            if (data.isEmpty) {
                message.warning("Não há linhas para atribuir ao vendedor");
            }
            else {
                loading.start();
                vendedorMovimentacaoService.atribuir(vendedor.vendedorId, { linhaIds: data.items.map(x => x.linhaId)})
                    .then(response => {
                        message.success("Atribuição registrada com sucesso");
                        modal.close();
                        onAtribuicaoRegistrada();
                        window.open(`/ProtocoloMovimentacao/${response.data.vendedorMovimentacaoId}`);
                    })
                    .catch(message.showBlobException)
                    .finally(loading.stop);
            }
        }
    }, [data.isEmpty, data.items, loading, message, modal, onAtribuicaoRegistrada, vendedor.vendedorId, vendedorMovimentacaoService]);

    const handleAdd = React.useCallback(() => {
        if (!loading.isLoading) {
            const numeroSemMascara = formatter.extractNumbers(itensForm.getInputValues().numeroSemMascara);

            console.log({
                numeroSemMascara,
                d: data.items
            })

            if (numeroSemMascara.length === 11 && data.items.find(x => x.numeroSemMascara === numeroSemMascara) === undefined) {
                loading.start();
                linhaService.obterParaAtribuir({numeroSemMascara})
                    .then(response => {
                        if (response.data !== null) {
                            data.addRow(response.data);
                        }

                        itensForm.reset();
                    })
                    .catch(message.showException)
                    .finally(loading.stop);
            }
        }
    }, [data, formatter, itensForm, linhaService, loading, message.showException]);

    const handleEnter = React.useCallback(() => {
        data.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ModalWindow 
            modal={modal}
            size="md"
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle title="Atribuição de Linhas" />
            
            <ModalWindowContent>
                <VerticalGrid>
                    <Fieldset
                        title="Linhas"
                        icon={<PhoneIcon />}
                        hasContentPadding={false}
                    >
                        <GridItem xs={12} className={clsx(themeColor.component.gridFilterBar, cls.fieldsetAddBox)}>
                            <HorizontalGrid spacing={1}>
                                <GridItem xs={3}>
                                    <TelefoneField 
                                        field={itensForm.fields.numeroSemMascara}
                                        inputRef={telefoneFieldRef}
                                        label="Número"
                                        maskType="cel"
                                        required
                                        updateFieldStateOnChange
                                    />
                                </GridItem>

                                <GridItem xs={1}>
                                    <IconButton onClick={handleAdd} color="primary">
                                        <AddCircleIcon />
                                    </IconButton>
                                </GridItem>
                            </HorizontalGrid>
                        </GridItem>

                        <GridItem xs={12}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>ID</TableHeaderCell>
                                        <TableHeaderCell>Número</TableHeaderCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                {
                                    data.items.map((linha) => {
                                        return (
                                            <TableRow key={linha.linhaId}>
                                                <TableKeyCell>{linha.linhaId}</TableKeyCell>
                                                <TableCell>{formatter.toCelular(linha.numeroSemMascara)}</TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                                </TableBody>
                            </Table>
                        </GridItem>
                    </Fieldset>
                </VerticalGrid>

            </ModalWindowContent>
            
            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm} disabled={data.itemsCount <= 0}>Atribuir Linhas</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>       
    )
}

const useStyles = makeStyles(theme => ({
    fieldsetAddBox: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}))